import type { HTMLAttributes, ReactNode } from 'react'
import { Surface } from './Surface'
import { Pill } from './Pill'
import { Sparkline, drawablePoints, type SparklineTone } from './Sparkline'
import { cx } from './cx'
import type { Tone } from './Pill'
import s from './StatTile.module.css'

/**
 * A KPI / metric tile — label over a big tabular-nums value, with an optional
 * sub-line. The one stat card used by dashboards, the funding KPIs, and cockpit
 * summaries, so every "headline number" reads the same. `tone` colours the value
 * to signal severity (e.g. a critical count that needs attention); `accent` is
 * the brand-tinted emphasis. Pass at most one.
 */
export interface StatTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  label: ReactNode; value: ReactNode; sub?: ReactNode; accent?: boolean; tone?: Tone
  /** A small glyph before the engraved label (the design's cell glyph). Decorative. */
  icon?: ReactNode
  /**
   * The figure's unit, set BESIDE it — "34 of 40 licensed", "9.4 days". Part of
   * the figure's own statement, so it sits in the same `<dd>`; muted and 700,
   * never the figure's size, so the number is still what the eye lands on.
   */
  unit?: ReactNode
  /** The change, as the design's delta pill ("+6%"). The HOST words it and
   *  picks its tone: a rise is not automatically good. */
  delta?: { label: ReactNode; tone?: Tone }
  /** A trend under the figure, from a series the HOST read (see `Sparkline`:
   *  fewer than two finite points draws nothing, never a placeholder line). */
  trend?: { points: readonly number[]; label: string; tone?: SparklineTone }
}

/* REST PROPS REACH THE ELEMENT, and that is not housekeeping (#2406).
   This signature used to destructure six named props and nothing else, so
   anything a host passed alongside them was dropped on the floor — silently,
   because a hyphenated JSX attribute like `data-testid` is not checked against
   a component's prop type. `Surface` below has always spread `...rest`; the
   props simply never got that far.

   It is load-bearing for the migration off `crm.module.css`'s hand-rolled fact
   list, which is the twenty-seventh container of this kind in the tree. Six of
   its call sites carry testids that browser specs locate by — `sla-block`,
   `sla-${obligation}`, `insight-sla`, `peek-health` — and every one of them
   would have vanished on adoption while the types stayed green and the unit
   suite stayed green, leaving a browser failure with no visible cause. */
export function StatTile({
  label, value, sub, accent, tone, icon, unit, delta, trend, className, ...rest
}: StatTileProps) {
  return (
    <Surface className={cx(s.tile, className)} {...rest}>
      {/* A MEASURE IS A NAME AND A VALUE, so it is a description list.
          This was three anonymous `<div>`s, which says nothing about what the
          number IS to anything that cannot see the layout — and the portal's
          own Home contract asks the cap's metric for its `<dd>` by name,
          because the hand-rolled row this component replaced was a `<dl>`.
          Adopting the shared tile quietly dropped the semantics the row had.
          One `<dt>`, and the sub-line is a SECOND `<dd>` on the same term:
          it is another statement about the same measure, not a new one. */}
      {/* The unit and the delta join the FIGURE's `<dd>`: "1,284 seats +6%" is
          one statement about the measure. The trend is a second `<dd>` for the
          same reason the sub-line is — another statement about the same term,
          made as an image with its own words. (#2406, "Dashboards and Cards":
          the metric card, single and with trend, and the strip's cells.) */}
      <dl className={s.pair}>
        <dt className={s.label}>
          {icon != null && <span className={s.glyph} aria-hidden="true">{icon}</span>}
          {label}
        </dt>
        {/* The VALUE stays the `<dd>`'s own first text: hosts and their tests
            read a metric as `<dt>label</dt><dd>value</dd>` (the estate KPI and
            MRR readers, the portal Home contract), so the unit and the delta
            are appended after it rather than the value being wrapped. */}
        <dd className={cx(s.value, (unit != null || delta != null) && s.figure, accent && s.accent, tone && s[`tone_${tone}`])}>
          {value}
          {unit != null && <span className={s.unit}>{unit}</span>}
          {delta != null && <Pill tone={delta.tone ?? 'neutral'} className={s.delta}>{delta.label}</Pill>}
        </dd>
        {trend != null && drawablePoints(trend.points).length >= 2 && (
          <dd className={s.trend}>
            <Sparkline points={trend.points} label={trend.label} tone={trend.tone} />
          </dd>
        )}
        {sub != null && <dd className={s.sub}>{sub}</dd>}
      </dl>
    </Surface>
  )
}
